<template>
  <div class="description">
    Do you want to create a break for all schedules during a time range? <br />
    This might be a dinner or lunch break that applies to all schedules.
    <p>
      This is an optional step. If you don't want to create this break now, click submit. <br />You
      can always add breaks later.
    </p>
  </div>
  <div class="event-days">
    <div
      v-for="(day, i) in sort(wizard.days, (i) => i.start_time.toISOString())"
      :key="i"
      class="day"
      :data-cy="`day-break-${i}`"
    >
      <div class="d-flex align-center gap-5">
        <div>
          <span class="title">Day {{ i + 1 }}</span>
          <span class="ml-2"
            >{{ dayjs(day.start_time).format("MMM D, h:mm A") }} -
            {{ dayjs(day.end_time).format("h:mm A") }}</span
          >
        </div>
        <v-btn
          size="small"
          @click="
            state.show = true;
            state.day = day;
          "
          :disabled="wizard.loading"
          data-cy="add-day-break-modal-btn"
        >
          Add
        </v-btn>
      </div>
      <div v-for="(b, j) in day.breaks" :key="j" class="d-flex align-center gap-4">
        <span>
          {{ dayjs(b.start_time).format("h:mm A") }} - {{ dayjs(b.end_time).format("h:mm A")
          }}{{ b.has_teachers || b.has_teams ? " - for " : "" }}{{ b.has_teachers ? "Teachers" : ""
          }}{{ b.has_teachers && b.has_teams ? " and " : "" }}{{ b.has_teams ? "Teams" : "" }}
        </span>
        <v-btn
          size="small"
          icon
          color="transparent"
          @click="day.breaks.splice(day.breaks.indexOf(b), 1)"
          ><v-icon>mdi-trash-can-outline</v-icon></v-btn
        >
      </div>
      <div v-if="!day.breaks.length">No Breaks</div>
    </div>
  </div>

  <v-dialog v-model="state.show" max-width="350px">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div>Add a Break</div>
        <v-btn @click="state.show = false" icon color="transparent"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </v-card-title>
      <v-card-item>
        <div class="m-3 d-flex flex-column gap-3 align-center">
          <DatePicker
            style="width: 150px"
            label="Start"
            v-model="state.start"
            type="time"
            :min="
              state.day?.start_time
                ? dayjs(state.day?.start_time).subtract(1, 'minutes').toDate()
                : undefined
            "
            :max="state.end"
            color="primary"
            data-cy="add-break-start"
          ></DatePicker>
          <DatePicker
            style="width: 150px"
            label="End"
            v-model="state.end"
            type="time"
            :min="dayjs(state.start).add(1, 'minutes').toDate()"
            :max="
              state.day?.end_time
                ? dayjs(state.day?.end_time)
                    .add(state.day.interval + 1, 'minutes')
                    .toDate()
                : undefined
            "
            color="primary"
            data-cy="add-break-end"
          ></DatePicker>
          <v-switch
            color="primary"
            class="custom-input checkbox"
            label="Apply breaks to all teachers"
            data-cy="event-wizard-breaks-all-teachers"
            density="compact"
            v-model="state.has_teachers"
          ></v-switch>
          <v-switch
            color="primary"
            class="custom-input checkbox"
            label="Apply breaks to all teams"
            data-cy="event-wizard-breaks-all-teams"
            density="compact"
            v-model="state.has_teams"
          ></v-switch>
        </div>
      </v-card-item>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="flat" @click="save" data-cy="wizard-add-break-modal-save-btn">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import DatePicker from "@/components/datePicker.vue";
import { useWizard, type EventDay } from "@/stores/school/wizard";
import { sort } from "@/utils/sort";
import dayjs from "dayjs";
import { watch } from "vue";
import { reactive } from "vue";

const wizard = useWizard();

const state = reactive({
  start: dayjs().set("hours", 13).set("minutes", 0).toDate(),
  end: dayjs().set("hours", 13).set("minutes", 30).toDate(),
  show: false,
  day: null as null | EventDay,
  has_teachers: false,
  has_teams: false
});

const save = () => {
  state.day?.breaks.push({
    start_time: state.start,
    end_time: state.end,
    has_teachers: state.has_teachers,
    has_teams: state.has_teams
  });
  state.show = false;
};

watch(
  () => state.day,
  () => {
    Object.assign(state, {
      start: dayjs(state.day?.start_time)
        // .add(1, "hours").set("minutes", 0).set("seconds", 0)
        .toDate(),
      end: dayjs(state.day?.end_time)
        // .add(2, "hours").set("minutes", 0).set("seconds", 0)
        .toDate()
    });
  }
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.event-days {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;

  .title {
    font-weight: 600;
    font-size: 1.1rem;
  }

  .day {
    background-color: $primary-50;
    padding: 20px;
    border-radius: 10px;
  }
}
</style>
